import React, { useState } from 'react';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    facilities: any[];
}

const initialForm = {
    facilityId: '',
    cropType: 'avocado',
    itemType: 'produce',
    quantityKg: '',
    reason: 'sale',
    destinationType: 'buyer',
    destinationId: '',
    farmerId: '',
    qualityGrade: 'A',
    temperature: '',
    humidity: '',
    expiryDate: '',
    batchReference: '',
    notes: '',
    lossCategory: '',
    lossValue: '',
    rootCause: '',
    preventionStrategy: ''
};

const RecordOutgoingModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, facilities }) => {
    const [formData, setFormData] = useState(initialForm);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const isLossReason = formData.reason === 'damage' || formData.reason === 'spoilage';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSubmit(formData);
            setFormData(initialForm);
            onClose();
        } catch (error) {
            console.error('Failed to record outgoing transaction:', error);
            alert(error instanceof Error ? error.message : 'Failed to record outgoing transaction');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                <div className="bg-gradient-to-r from-[#FFD700] to-yellow-500 px-6 py-5 rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                                <ArrowUpTrayIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-white">Record Outgoing Transaction</h2>
                                <p className="text-yellow-100 text-sm">Track sale, transfer, distribution, and losses</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition">
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Facility *</label>
                            <select
                                value={formData.facilityId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, facilityId: e.target.value }))}
                                required
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="">Select facility</option>
                                {facilities.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Item Type *</label>
                            <select
                                value={formData.itemType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, itemType: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="produce">Produce</option>
                                <option value="fertilizer">Fertilizer</option>
                                <option value="compost">Compost</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Crop Type *</label>
                            <select
                                value={formData.cropType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, cropType: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="avocado">Avocado</option>
                                <option value="macadamia">Macadamia</option>
                                <option value="vegetables">Vegetables</option>
                                <option value="fertilizer">Fertilizer</option>
                                <option value="compost">Compost</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Quantity (kg) *</label>
                            <input
                                type="number"
                                value={formData.quantityKg}
                                onChange={(e) => setFormData((prev) => ({ ...prev, quantityKg: e.target.value }))}
                                required
                                min="0"
                                step="0.01"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Reason *</label>
                            <select
                                value={formData.reason}
                                onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="sale">Sale</option>
                                <option value="issue_to_farmer">Issue to Farmer</option>
                                <option value="compost_distribution">Compost Distribution</option>
                                <option value="transfer">Transfer</option>
                                <option value="donation">Donation</option>
                                <option value="damage">Damage</option>
                                <option value="spoilage">Spoilage</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Destination Type</label>
                            <select
                                value={formData.destinationType}
                                onChange={(e) => setFormData((prev) => ({ ...prev, destinationType: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="buyer">Buyer</option>
                                <option value="warehouse">Warehouse</option>
                                <option value="farmer">Farmer</option>
                                <option value="distribution_point">Distribution Point</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Destination ID</label>
                            <input
                                type="number"
                                value={formData.destinationId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, destinationId: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Batch Reference</label>
                            <input
                                type="text"
                                value={formData.batchReference}
                                onChange={(e) => setFormData((prev) => ({ ...prev, batchReference: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Farmer ID (optional)</label>
                            <input
                                type="number"
                                value={formData.farmerId}
                                onChange={(e) => setFormData((prev) => ({ ...prev, farmerId: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Grade</label>
                            <select
                                value={formData.qualityGrade}
                                onChange={(e) => setFormData((prev) => ({ ...prev, qualityGrade: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            >
                                <option value="A">A</option>
                                <option value="B">B</option>
                                <option value="C">C</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Temp (°C)</label>
                            <input
                                type="number"
                                value={formData.temperature}
                                onChange={(e) => setFormData((prev) => ({ ...prev, temperature: e.target.value }))}
                                step="0.1"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Humidity (%)</label>
                            <input
                                type="number"
                                value={formData.humidity}
                                onChange={(e) => setFormData((prev) => ({ ...prev, humidity: e.target.value }))}
                                step="0.1"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date</label>
                            <input
                                type="date"
                                value={formData.expiryDate}
                                onChange={(e) => setFormData((prev) => ({ ...prev, expiryDate: e.target.value }))}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {isLossReason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-red-900">Loss Analysis</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    value={formData.lossCategory}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, lossCategory: e.target.value }))}
                                    className="px-4 py-2 border border-gray-300 rounded-lg"
                                    placeholder="Loss category"
                                />
                                <input
                                    type="number"
                                    value={formData.lossValue}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, lossValue: e.target.value }))}
                                    className="px-4 py-2 border border-gray-300 rounded-lg"
                                    placeholder="Loss value (RWF)"
                                />
                            </div>
                            <textarea
                                value={formData.rootCause}
                                onChange={(e) => setFormData((prev) => ({ ...prev, rootCause: e.target.value }))}
                                rows={2}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                placeholder="Root cause"
                            />
                            <textarea
                                value={formData.preventionStrategy}
                                onChange={(e) => setFormData((prev) => ({ ...prev, preventionStrategy: e.target.value }))}
                                rows={2}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                                placeholder="Prevention strategy"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition font-medium"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 bg-gradient-to-r from-[#FFD700] to-yellow-500 text-white rounded-lg hover:from-yellow-600 hover:to-yellow-600 transition font-medium shadow-lg disabled:opacity-50"
                        >
                            {loading ? 'Recording...' : 'Record Transaction'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecordOutgoingModal;
